/**
 * Modular type scale with two ends.
 *
 * Every text style sits on a step of the scale. Its size is computed twice: once from the phone
 * base size and ratio (stored as `size.min`, used below 769px) and once from the desktop base size
 * and ratio (stored as `size.max`, used from 1281px; the midpoint applies in between). A smaller
 * ratio on phones keeps headings in proportion to the narrow screen, a larger one on desktop gives
 * display headings their contrast.
 *
 * Steps may be fractional: control at -0.5 sits just below body text, accounting for the visual
 * weight of buttons and fields. Heading 1 at +4 skips step 3 on purpose, so display headings
 * separate clearly from section headings.
 */

export const TEXT_STYLE_NAMES = ['heading1', 'heading2', 'heading3', 'body', 'control', 'caption', 'preamble'];

export const DEFAULT_SCALE_STEPS = {
  heading1: 4,
  heading2: 2,
  heading3: 1,
  body: 0,
  control: -0.5,
  caption: -1,
  preamble: -1.5,
};

export const DEFAULT_SCALE = {
  phone_base: 17,
  phone_ratio: 1.2,
  desktop_base: 20,
  desktop_ratio: 1.333,
  steps: { ...DEFAULT_SCALE_STEPS },
};

/** Musical intervals offered as ratios; `name` is the i18n key of the interval */
export const SCALE_RATIOS = [
  { value: 1.067, name: 'minor_second' },
  { value: 1.125, name: 'major_second' },
  { value: 1.2, name: 'minor_third' },
  { value: 1.25, name: 'major_third' },
  { value: 1.333, name: 'perfect_fourth' },
  { value: 1.414, name: 'augmented_fourth' },
  { value: 1.5, name: 'perfect_fifth' },
  { value: 1.618, name: 'golden_ratio' },
];

/**
 * Font size limits per text style. Past roughly 2× the default the caption and control styles stop
 * reading as secondary and the whole scale inverts, so they are capped.
 */
export const FONT_SIZE_CONSTRAINTS = {
  heading1: { min: 14, max: 160 },
  heading2: { min: 12, max: 120, smaller_or_equal_to: 'heading1' },
  heading3: { min: 12, max: 80, smaller_or_equal_to: 'heading2' },
  body: { min: 12, max: 40, smaller_or_equal_to: 'heading3' },
  preamble: { min: 12, max: 40, smaller_or_equal_to: 'body' },
  control: { min: 12, max: 32, smaller_or_equal_to: 'body' },
  caption: { min: 12, max: 32, smaller_or_equal_to: 'body' },
};

/**
 * Fill in missing or invalid fields of a stored scale. Sites saved before the scale had two ends
 * only stored `typography_base_font_size` and a single `typography_scale_ratio` ('custom' when
 * none was chosen); those seed the desktop end.
 * @param {any} scale
 * @param {{typography_scale_ratio?: string, typography_base_font_size?: number}} [legacy]
 */
export function normalize_scale(scale, legacy = {}) {
  const legacy_ratio = parseFloat(legacy.typography_scale_ratio);
  const number = (value, fallback) => (Number.isFinite(Number(value)) && Number(value) > 0 ? Number(value) : fallback);
  const steps = {};
  for (const name of TEXT_STYLE_NAMES) {
    const step = Number(scale?.steps?.[name]);
    steps[name] = Number.isFinite(step) ? step : DEFAULT_SCALE_STEPS[name];
  }
  return {
    phone_base: number(scale?.phone_base, DEFAULT_SCALE.phone_base),
    phone_ratio: number(scale?.phone_ratio, DEFAULT_SCALE.phone_ratio),
    desktop_base: number(scale?.desktop_base, number(legacy.typography_base_font_size, DEFAULT_SCALE.desktop_base)),
    desktop_ratio: number(scale?.desktop_ratio, Number.isFinite(legacy_ratio) ? legacy_ratio : DEFAULT_SCALE.desktop_ratio),
    steps,
  };
}

/**
 * @param {number} base
 * @param {number} ratio
 * @param {number} step
 * @param {{min: number, max: number}} [constraint]
 */
function scaled_size(base, ratio, step, constraint) {
  const size = base * Math.pow(ratio, step);
  const clamped = constraint ? Math.max(constraint.min, Math.min(constraint.max, size)) : size;
  return Math.round(clamped);
}

/**
 * Sizes in px for every text style: `min` from the phone end, `max` from the desktop end.
 * @param {ReturnType<typeof normalize_scale>} scale
 * @returns {Record<string, {min: number, max: number}>}
 */
export function compute_scale_sizes(scale) {
  const sizes = {};
  for (const name of TEXT_STYLE_NAMES) {
    const step = scale.steps[name];
    const constraint = FONT_SIZE_CONSTRAINTS[name];
    sizes[name] = {
      min: scaled_size(scale.phone_base, scale.phone_ratio, step, constraint),
      max: scaled_size(scale.desktop_base, scale.desktop_ratio, step, constraint),
    };
  }
  return sizes;
}

/**
 * Whether every text style currently uses the size the scale gives it.
 * @param {Record<string, {size: {min: number, max: number}}>} text_styles
 * @param {ReturnType<typeof normalize_scale>} scale
 */
export function sizes_follow_scale(text_styles, scale) {
  const sizes = compute_scale_sizes(scale);
  return TEXT_STYLE_NAMES.every(name =>
    !text_styles[name] ||
    (text_styles[name].size?.min === sizes[name].min && text_styles[name].size?.max === sizes[name].max)
  );
}
